import { z } from 'zod';

export const JOB_STATUSES = ['queued', 'running', 'done', 'failed'] as const;
export const JobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof JobStatusSchema>;

/** Known job kinds; the column is free text so later bolts add kinds without a migration. */
export const JOB_KINDS = [
  'type_widening',
  'status_migration',
  'issue_type_move',
  'index_backfill',
  // Bolt 5 (S-05-13): portal jobs, run inline for <= 100 targets, else by the jobs worker.
  'bulk_edit',
  'bulk_transition',
  'bulk_assign',
  'export_csv',
  // Bolt 8 H2b (S-07-07): re-evaluates a `date_field` automation rule against every ticket
  // that already carries the watched value, arming/re-arming its `timer` row.
  'date_field_backfill',
  // Bolt 9 (S-09-02, S-09-04, S-09-05): the import framework's three chunked jobs, one per
  // `import_batch` lifecycle stage. Payload is always `{ principal, batchId }`; batch state
  // (discovery/mapping/report/stats/cursor) lives on `import_batch`, not `job.progress`.
  'import.discover',
  'import.dry_run',
  'import.run',
] as const;
export const JobKindSchema = z.enum(JOB_KINDS);
export type JobKind = z.infer<typeof JobKindSchema>;

/**
 * Long-running, chunked configuration work (ADR-0013 §5). Bolt 4 runs jobs inline when
 * there is nothing to iterate; Bolt 5 adds the worker.
 */
export const JobSchema = z.object({
  id: z.uuid(),
  kind: z.string().min(1).max(80),
  status: JobStatusSchema,
  /** Kind-specific input, e.g. `{ fieldId, from, to }` for a type widening. */
  payload: z.record(z.string(), z.unknown()),
  /** `{ done, total, cursor }` while running; kind-specific afterwards. */
  progress: z.record(z.string(), z.unknown()),
  attempts: z.int().min(0),
  error: z.string().nullable(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type Job = z.infer<typeof JobSchema>;

/** Targets at or below this count run inline in the API request; above it the job is queued. */
export const JOB_INLINE_MAX_TARGETS = 100;
/** Tickets per transaction inside bulk executors (S-05-13). */
export const BULK_CHUNK_SIZE = 100;
/** Rows an export writes at most (S-05-13); fetched in pages of `EXPORT_PAGE_SIZE`. */
export const EXPORT_MAX_ROWS = 50_000;
export const EXPORT_PAGE_SIZE = 200;
/** Presigned download validity of an export (seconds). */
export const EXPORT_DOWNLOAD_TTL_SECONDS = 3600;

/** One failed target of a bulk job, recorded in `progress.failures`. */
export const JobFailureItemSchema = z.object({
  ticketId: z.uuid(),
  code: z.string(),
  message: z.string(),
});
export type JobFailureItem = z.infer<typeof JobFailureItemSchema>;

/** `GET /jobs?status=`. */
export const JobListQuerySchema = z.object({
  status: JobStatusSchema.optional(),
});
export type JobListQuery = z.infer<typeof JobListQuerySchema>;
